import * as THREE from 'three'
import type { TrackPath } from './trackImporter'
import { createTrackPlaybackStore, type TrackPlaybackState } from './trackPlayback'
import { trackRegionConfig, trackStartAccelerationDuration } from '../../config/constants'
import {
    getTrackRegionStateAtPosition,
    resolveTrackRegionConfig,
    type ResolvedTrackRegionConfig,
    type TrackRegionState,
} from './trackRegions'

export type {
    TrackRegionConfig,
    TrackRegionState,
    TrackRegionWeight,
    TrackTransitionPointRange,
} from './trackRegions'

export type { TrackPlaybackState } from './trackPlayback'
export type TrackFrame = {
    position: THREE.Vector3
    tangent: THREE.Vector3
    up: THREE.Vector3
    trackRight: THREE.Vector3
    trackQuaternion: THREE.Quaternion
    cameraQuaternion: THREE.Quaternion
}

export type TrackController = {
    hasTrackPath: () => boolean
    getTrackPath: () => TrackPath
    getTrackLength: () => number
    getTrackTravelLength: () => number
    beginEndDeceleration: () => void
    clearEndDeceleration: () => void
    getResolvedTrackRegionConfig: () => ResolvedTrackRegionConfig
    getTrackRegionState: (trackPosition?: number) => TrackRegionState
    frameAtPosition: (position: number) => TrackFrame
    trackQuaternionAtPosition: (
        position: number,
        target?: THREE.Quaternion,
    ) => THREE.Quaternion
    cameraQuaternionAtPosition: (
        position: number,
        target?: THREE.Quaternion,
    ) => THREE.Quaternion
    positionDeltaOnTrack: (a: number, b: number) => number
    normalizeTrackPosition: (position: number) => number
    getTrackPlaybackState: () => TrackPlaybackState
    subscribeTrackPlayback: (
        listener: (state: TrackPlaybackState) => void,
    ) => () => void
    setTrackPaused: (paused: boolean) => void
    toggleTrackPaused: () => boolean
    setTrackSpeed: (speed: number) => void
    setTrackStartSpeed: (speed: number) => void
    setTrackEndSpeed: (speed: number) => void
    setTrackPosition: (position: number) => void
    restartTrack: () => void
    updateTrackPosition: (deltaSeconds: number) => number
    setSpeedMultiplier: (multiplier: number) => void
}

const frameTangentSampleDistance = 0.35

export function createTrackController(trackPath: TrackPath): TrackController {
    const currentTrackPath = trackPath
    const currentTrackRegionPositions = resolveTrackRegionConfig(
        trackRegionConfig,
        currentTrackPath,
    )

    const hasTrackPath = () => true

    const getTrackPath = () => currentTrackPath

    const getTrackLength = () => getTrackPath().pathEndDistance

    const getTrackTravelLength = () =>
        Math.max(getTrackLength(), playbackStore.getState().trackPosition)

    const normalizeTrackPosition = (position: number) => {
        return Math.max(position, 0)
    }

    const getResolvedTrackRegionConfig = () => {
        return currentTrackRegionPositions
    }

    const resolveTrackSpeed = (trackPosition: number) => {
        const playbackState = playbackStore.getState()

        if (!hasTrackPath()) {
            return playbackState.startSpeed * _speedMultiplier
        }

        const trackLength = getTrackLength()
        const clampedPosition = THREE.MathUtils.clamp(
            trackPosition,
            0,
            trackLength,
        )
        const progress =
            trackLength <= 0 ? 0 : clampedPosition / trackLength

        const baseSpeed = THREE.MathUtils.lerp(
            playbackState.startSpeed,
            playbackState.endSpeed,
            progress,
        )
        const startAccelerationProgress =
            trackStartAccelerationDuration > 0
                ? THREE.MathUtils.clamp(
                    playbackState.elapsedSeconds / trackStartAccelerationDuration,
                    0,
                    1,
                )
                : 1
        return baseSpeed * startAccelerationProgress * _speedMultiplier
    }

    const playbackStore = createTrackPlaybackStore({
        hasTrackPath,
        normalizeTrackPosition,
        resolveTrackSpeed,
    })

    let _speedMultiplier = 1.0
    const quaternionBasisMatrix = new THREE.Matrix4()
    const cameraForward = new THREE.Vector3()
    const cameraRight = new THREE.Vector3()

    const beginEndDeceleration = () => {
        _speedMultiplier = 1
        playbackStore.sync()
    }

    const clearEndDeceleration = () => {
        _speedMultiplier = 1
        playbackStore.sync()
    }

    const setSpeedMultiplier = (multiplier: number) => {
        _speedMultiplier = multiplier
        playbackStore.sync()
    }

    const getTrackRegionState = (
        trackPosition = playbackStore.getState().trackPosition,
    ) =>
        getTrackRegionStateAtPosition(
            Math.min(trackPosition, getTrackLength()),
            getResolvedTrackRegionConfig(),
            getTrackLength(),
        )

    const resolveDistanceOnPath = (distance: number) => {
        const path = getTrackPath()
        const resolvedDistance = THREE.MathUtils.clamp(
            distance,
            0,
            getTrackLength(),
        )
        const samples = path.lengthSamples

        let low = 0
        let high = samples.length - 1

        while (low < high) {
            const mid = Math.floor((low + high) / 2)

            if (samples[mid].distance < resolvedDistance) {
                low = mid + 1
            } else {
                high = mid
            }
        }

        const next = samples[low]
        const previous = samples[Math.max(0, low - 1)]
        const span = Math.max(next.distance - previous.distance, 0.000001)
        const blend = THREE.MathUtils.clamp(
            (resolvedDistance - previous.distance) / span,
            0,
            1,
        )
        const t = THREE.MathUtils.lerp(previous.t, next.t, blend)

        return { segment: path.segments[next.segmentIndex], t }
    }

    const evaluateBezier = (
        segment: TrackPath['segments'][number],
        t: number,
    ) => {
        const inv = 1 - t

        return new THREE.Vector3()
            .addScaledVector(segment.p0, inv * inv * inv)
            .addScaledVector(segment.h0, 3 * inv * inv * t)
            .addScaledVector(segment.h1, 3 * inv * t * t)
            .addScaledVector(segment.p1, t * t * t)
    }

    const pointAtDistance = (distance: number) => {
        const { segment, t } = resolveDistanceOnPath(distance)
        return evaluateBezier(segment, t)
    }

    const tangentAtDistance = (
        distance: number,
        fallbackRotation: THREE.Quaternion,
    ) => {
        const trackLength = getTrackLength()
        const sampleDistance = Math.min(
            frameTangentSampleDistance,
            trackLength * 0.01,
        )
        const beforeDistance = THREE.MathUtils.clamp(
            distance - sampleDistance,
            0,
            trackLength,
        )
        const afterDistance = THREE.MathUtils.clamp(
            distance + sampleDistance,
            0,
            trackLength,
        )
        const tangent = pointAtDistance(afterDistance).sub(
            pointAtDistance(beforeDistance),
        )

        return tangent.lengthSq() > 0.000001
            ? tangent.normalize()
            : new THREE.Vector3(0, 0, 1)
                .applyQuaternion(fallbackRotation)
                .normalize()
    }

    const frameAtPosition = (position: number) => {
        const trackLength = getTrackLength()
        const resolvedDistance = THREE.MathUtils.clamp(position, 0, trackLength)
        const { segment, t } = resolveDistanceOnPath(resolvedDistance)
        const positionPoint = evaluateBezier(segment, t)
        const keyRotation = new THREE.Quaternion()
            .slerpQuaternions(segment.q0, segment.q1, t)
            .normalize()
        const tangent = tangentAtDistance(resolvedDistance, keyRotation)
        const importedUp = new THREE.Vector3(0, 1, 0).applyQuaternion(
            keyRotation,
        )

        importedUp.addScaledVector(tangent, -importedUp.dot(tangent))

        if (importedUp.lengthSq() < 0.000001) {
            importedUp.set(0, 1, 0).addScaledVector(tangent, -tangent.y)
        }

        const up = importedUp.normalize()
        const trackRight = new THREE.Vector3()
            .crossVectors(up, tangent)
            .normalize()
        const correctedUp = new THREE.Vector3()
            .crossVectors(tangent, trackRight)
            .normalize()
        const trackQuaternion = new THREE.Quaternion()
        const cameraQuaternion = new THREE.Quaternion()

        quaternionBasisMatrix.makeBasis(trackRight, correctedUp, tangent)
        trackQuaternion.setFromRotationMatrix(quaternionBasisMatrix)

        quaternionBasisMatrix.makeBasis(
            cameraRight.copy(trackRight).negate(),
            correctedUp,
            cameraForward.copy(tangent).negate(),
        )
        cameraQuaternion.setFromRotationMatrix(quaternionBasisMatrix)

        if (position > trackLength) {
            positionPoint.addScaledVector(tangent, position - trackLength)
        }

        return {
            position: positionPoint,
            tangent,
            up: correctedUp,
            trackRight,
            trackQuaternion,
            cameraQuaternion,
        }
    }

    const trackQuaternionAtPosition = (
        position: number,
        target = new THREE.Quaternion(),
    ) => {
        return target.copy(frameAtPosition(position).trackQuaternion)
    }

    const cameraQuaternionAtPosition = (
        position: number,
        target = new THREE.Quaternion(),
    ) => {
        return target.copy(frameAtPosition(position).cameraQuaternion)
    }

    const positionDeltaOnTrack = (a: number, b: number) => {
        return Math.abs(
            normalizeTrackPosition(a) - normalizeTrackPosition(b),
        )
    }

    const setTrackPosition = (position: number) => {
        playbackStore.setPosition(position)
    }

    const restartTrack = () => {
        _speedMultiplier = 1
        playbackStore.restart()
    }

    playbackStore.sync({
        trackPosition: normalizeTrackPosition(
            playbackStore.getState().trackPosition,
        ),
    })

    return {
        hasTrackPath,
        getTrackPath,
        getTrackLength,
        getTrackTravelLength,
        beginEndDeceleration,
        clearEndDeceleration,
        getResolvedTrackRegionConfig,
        getTrackRegionState,
        frameAtPosition,
        trackQuaternionAtPosition,
        cameraQuaternionAtPosition,
        positionDeltaOnTrack,
        normalizeTrackPosition,
        getTrackPlaybackState: playbackStore.getState,
        subscribeTrackPlayback: playbackStore.subscribe,
        setTrackPaused: playbackStore.setPaused,
        toggleTrackPaused: playbackStore.togglePaused,
        setTrackSpeed: playbackStore.setSpeed,
        setTrackStartSpeed: playbackStore.setStartSpeed,
        setTrackEndSpeed: playbackStore.setEndSpeed,
        setTrackPosition,
        restartTrack,
        updateTrackPosition: playbackStore.updatePosition,
        setSpeedMultiplier,
    }
}
